import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React, { useEffect, useRef, useState } from 'react';

import {
  EsriAttribute,
  EsriBoundedBy,
  EsriGenerateRequest,
  EsriLocation,
  EsriRegions,
  generateEsriLocations,
} from '../../../../api/ach/esri';
import useBatchLocationJob from '../../../../hooks/useBatchLocationJob';
import { useConfig } from '../../../../hooks/useConfig';
import { useConstants } from '../../../../hooks/useConstants';
import { toLocationCsvFile } from '../../../../util/esriLocationCsv';
import posthog from '../../../../util/posthog';
import FormEndOptions from '../../../forms/FormEndOptions';
import { generateLocationUploadKey, uploadLocationFileToS3, validateLocationFile } from '../locationBatchUpload';
import EsriAttributeSearch from './EsriAttributeSearch';
import EsriLocationUpload from './EsriLocationUpload';
import EsriRegionInput from './EsriRegionInput';

// Builds an ESRI audience location file and attaches it to an ad group:
// ACH /esri/generate returns block-group rows, the browser turns them into the
// platform location CSV, and that file goes through the same S3 + Ventura
// batch_create path as a manual Batch Add. Opened from the Locations tab, and as
// the next step of AI Audience search with its ESRI picks prefilled.

const EMPTY_REGIONS: EsriRegions = { states: [], dmas: [], zips: [] };

// PostHog properties describing a generate request; ESRI-in-platform usage is
// compared against BigQuery ESRI query volume on these.
const describeRequest = ({ attributes, regions, locations, boundedBy, namePrefix }: EsriGenerateRequest) => ({
  attribute_count: attributes.length,
  categories: [...new Set(attributes.map((a) => a.category ?? 'unknown'))],
  geo_types: (['states', 'dmas', 'zips'] as const).filter((type) => regions[type].length > 0),
  state_count: regions.states.length,
  dma_count: regions.dmas.length,
  zip_count: regions.zips.length,
  uploaded_location_count: locations.length,
  bounded_by: boundedBy || 'none',
  has_name_prefix: namePrefix.length > 0,
});

type AttachStage = 'validate' | 'upload' | 'batch_create';

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
  { field: 'latitude', headerName: 'Latitude', width: 110, type: 'number' },
  { field: 'longitude', headerName: 'Longitude', width: 110, type: 'number' },
  { field: 'radius', headerName: 'Radius (m)', width: 100, type: 'number' },
  { field: 'zip', headerName: 'Zip', width: 80 },
  { field: 'state', headerName: 'State', flex: 1, minWidth: 100 },
  { field: 'dma', headerName: 'DMA', flex: 1, minWidth: 140 },
];

export interface EsriLocationBuilderProps {
  adGroupId: number;
  adGroupName: string;
  lineItemId: number;
  momentsType: string;
  source: 'locations_tab' | 'ai_audience';
  initialAttributes?: EsriAttribute[];
  initialNamePrefix?: string;
  onBack?: () => void;
  onClose: () => void;
}

function EsriLocationBuilder({
  adGroupId,
  adGroupName,
  lineItemId,
  momentsType,
  source,
  initialAttributes = [],
  initialNamePrefix = '',
  onBack,
  onClose,
}: EsriLocationBuilderProps) {
  const { apiHost, achApiHost } = useConfig();
  const { constants } = useConstants();
  const [attributes, setAttributes] = useState<EsriAttribute[]>(initialAttributes);
  const [regions, setRegions] = useState<EsriRegions>(EMPTY_REGIONS);
  const [locations, setLocations] = useState<EsriLocation[]>([]);
  const [boundedBy, setBoundedBy] = useState<EsriBoundedBy>('');
  const [namePrefix, setNamePrefix] = useState(initialNamePrefix.slice(0, 50));
  const generateStartedAt = useRef(0);
  const attachStage = useRef<AttachStage>('validate');

  const track = (event: string, properties: Record<string, unknown> = {}) =>
    posthog.capture(event, { source, ad_group_id: adGroupId, line_item_id: lineItemId, ...properties });

  useEffect(() => {
    track('esri_builder_opened', { prefilled_attribute_count: initialAttributes.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = useMutation({
    mutationFn: (request: EsriGenerateRequest) => generateEsriLocations(achApiHost, request),
    onMutate: () => {
      generateStartedAt.current = Date.now();
    },
    onSuccess: (data, request) =>
      track('esri_locations_generated', {
        ...describeRequest(request),
        row_count: data.rows.length,
        warning: Boolean(data.metadata.warning),
        duration_ms: Date.now() - generateStartedAt.current,
      }),
    onError: (err: Error, request) =>
      track('esri_generate_failed', { ...describeRequest(request), error: err.message }),
  });
  // The request the shown rows came from; its boundedBy goes into the CSV.
  const generated = generate.data && generate.variables;
  const rows = generate.data?.rows ?? [];
  const attachProperties = () => ({ ...describeRequest(generated), row_count: rows.length });

  const { start, alert, setAlert, isStarting } = useBatchLocationJob(adGroupId, false, (status) =>
    track('esri_locations_attached', { ...attachProperties(), status })
  );

  const attachFailed = (stage: AttachStage, message: string) => {
    track('esri_locations_attach_failed', { ...attachProperties(), stage, error: message });
    setAlert({ severity: 'error', message });
  };

  const attach = useMutation({
    mutationFn: async () => {
      attachStage.current = 'validate';
      const file = toLocationCsvFile(rows, generated.boundedBy);
      const validation = await validateLocationFile(apiHost, momentsType, file);
      if (!validation.valid) throw new Error(validation.message);
      attachStage.current = 'upload';
      const prefix = get(constants, 'LOCATION.VENTURA_UPLOADS_PREFIX_LOCATIONS', '');
      const key = `${generateLocationUploadKey(prefix, lineItemId, adGroupId, adGroupName)}.csv`;
      await uploadLocationFileToS3(apiHost, get(constants, 'S3_DIRECT_UPLOAD_URL'), file, key);
      return key;
    },
    onMutate: () => setAlert(null),
    onSuccess: (key) =>
      start(key, {
        onSuccess: () => track('esri_locations_attach_started', attachProperties()),
        onError: (err: Error) => {
          // Unlock the form so the same rows can be retried.
          attach.reset();
          attachFailed('batch_create', err.message || 'Could not start the location batch.');
        },
      }),
    onError: (err: Error) => attachFailed(attachStage.current, err.message || 'Upload failed.'),
  });

  const attaching = attach.isLoading || isStarting;
  const attached = attach.isSuccess;
  const locked = generate.isLoading || attaching || attached;
  const canAttach = rows.length > 0 && !locked;

  // Any edit invalidates the shown rows, so what gets attached always matches the form.
  const edit =
    <T,>(setter: (value: T) => void) =>
      (value: T) => {
        setter(value);
        generate.reset();
      };

  const onAttributesChange = (next: EsriAttribute[]) => {
    next
      .filter((attribute) => !attributes.some((current) => current.attribute_name === attribute.attribute_name))
      .forEach((attribute) =>
        track('esri_attribute_selected', { attribute_name: attribute.attribute_name, category: attribute.category })
      );
    edit(setAttributes)(next);
  };

  const regionCount = regions.states.length + regions.dmas.length + regions.zips.length;
  const canGenerate = attributes.length > 0 && (regionCount > 0 || locations.length > 0) && !locked;

  return (
    <Stack sx={{ width: { xs: '90vw', md: 900 }, maxWidth: '100%' }} justifyContent="space-between">
      <Stack p={2} spacing={2}>
        <Typography variant="h5" fontWeight="bold">
          ESRI Audience Locations for {adGroupName}
        </Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">Audience</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Demographics within a category are OR&apos;d together; different categories (demographics vs. market
            segments) are AND&apos;d.
          </Typography>
          <EsriAttributeSearch value={attributes} onChange={onAttributesChange} disabled={locked} />
        </Paper>

        <Typography variant="caption" color="text.secondary">
          Geographic scope: use regions, a location file, or both. At least one is required.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">Regions</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            OR within a type; AND across types. Block groups whose center falls inside the selected regions are kept.
          </Typography>
          <EsriRegionInput regions={regions} onChange={edit(setRegions)} disabled={locked} />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">Location file</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            CSV with latitude, longitude and radius (meters) columns, and optionally name. Block groups that overlap any
            point&apos;s radius are kept; with regions also set, only those inside the regions. Max 20,000 rows.
          </Typography>
          <EsriLocationUpload locations={locations} onChange={edit(setLocations)} disabled={locked} />
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }} disabled={locked}>
            <InputLabel id="esri-bounded-by-label">Bound targeting by</InputLabel>
            <Select
              labelId="esri-bounded-by-label"
              label="Bound targeting by"
              value={boundedBy}
              onChange={(e) => edit(setBoundedBy)(e.target.value as EsriBoundedBy)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="zipcode">Zipcode</MenuItem>
              <MenuItem value="state">State</MenuItem>
              <MenuItem value="dma">DMA</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Name prefix"
            value={namePrefix}
            onChange={(e) => edit(setNamePrefix)(e.target.value)}
            disabled={locked}
            placeholder="optional"
            inputProps={{ maxLength: 50 }}
            sx={{ flex: 1 }}
          />
        </Stack>

        {generate.isLoading && (
          <Typography variant="body2" color="text.secondary">
            Querying ESRI data... this may take up to 2 minutes.
          </Typography>
        )}
        {generate.isError && <Alert severity="error">{(generate.error as Error).message}</Alert>}

        {alert && (
          <Alert
            severity={alert.severity}
            action={alert.severity === 'info' ? <CircularProgress color="inherit" size={24} /> : undefined}
          >
            {alert.message}
          </Alert>
        )}

        {generated && rows.length === 0 && (
          <Alert severity="info">
            No matching block groups found. Try broadening the audience, regions or location file.
          </Alert>
        )}

        {generated && rows.length > 0 && (
          <Box>
            {generate.data.metadata.warning && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {generate.data.metadata.warning}
              </Alert>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>
              {rows.length.toLocaleString()} block groups
            </Typography>
            <DataGrid
              rows={rows.map((row, id) => ({ id, ...row }))}
              columns={columns}
              density="compact"
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
              sx={{ height: 400 }}
            />
          </Box>
        )}
      </Stack>

      <FormEndOptions>
        <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
          {onBack && (
            <Button variant="outlined" onClick={onBack} disabled={attaching}>
              Back
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={!canGenerate}
            onClick={() =>
              generate.mutate({ attributes, regions, locations, boundedBy, namePrefix: namePrefix.trim() })
            }
          >
            {generate.isLoading ? <CircularProgress color="inherit" size={20} /> : 'Generate'}
          </Button>
          <Button variant="contained" disabled={!canAttach} onClick={() => attach.mutate()}>
            {attaching ? <CircularProgress color="inherit" size={20} /> : 'Add to ad group'}
          </Button>
        </Stack>
      </FormEndOptions>
    </Stack>
  );
}

export default EsriLocationBuilder;
